import React from 'react';

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// Enhanced Dashboard Stats Skeleton
export const DashboardStatsSkeleton: React.FC = () => (
  <div className="max-w-7xl mx-auto px-6 py-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </div>
            <Skeleton className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg ml-2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Analytics Loading Skeleton
export const AnalyticsSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Performance Metrics Skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-8 w-16 mb-1" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>

    {/* Earnings Analytics Skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <Skeleton className="h-6 w-40 mb-4" />
          <Skeleton className="h-10 w-24 mb-2" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>

    {/* Chart Skeleton */}
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
      <Skeleton className="h-6 w-48 mb-4" />
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <Skeleton className="h-4 w-20" />
            <div className="flex-1 mx-4">
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Errand List Skeleton
export const ErrandListSkeleton: React.FC = () => (
  <div className="space-y-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-neutral-50 rounded-lg gap-3">
        <div className="flex-1 min-w-0">
          <Skeleton className="h-5 w-48 mb-2" />
          <Skeleton className="h-4 w-64 mb-1" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
    ))}
  </div>
);

// Individual Errand Card Skeleton (for compatibility)
export const ErrandCardSkeleton: React.FC = () => (
  <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200">
    <div className="flex-1 min-w-0">
      <Skeleton className="h-5 w-32 mb-1" />
      <Skeleton className="h-4 w-48 mb-1" />
      <Skeleton className="h-3 w-24" />
    </div>
    <div className="flex items-center space-x-2 flex-shrink-0">
      <Skeleton className="h-6 w-16 rounded-full" />
      <Skeleton className="h-4 w-12" />
    </div>
  </div>
);

// Enhanced Status Badge with Animation
interface StatusBadgeProps {
  status: string;
  animated?: boolean;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  animated = false,
  className = ''
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPulseColor = (status: string) => {
    switch (status) {
      case 'in_progress': return 'bg-purple-400';
      case 'pending': return 'bg-yellow-400';
      default: return '';
    }
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)} ${className}`}>
      {animated && (status === 'in_progress' || status === 'pending') && (
        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${getPulseColor(status)} animate-pulse`} />
      )}
      {status.replace('_', ' ')}
    </span>
  );
};

// Loading Spinner with Message
interface LoadingSpinnerProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = 'Loading...',
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="flex items-center space-x-3">
        <div className={`${sizeClasses[size]} border-2 border-blue-600 border-t-transparent rounded-full animate-spin`} />
        <span className="text-neutral-600">{message}</span>
      </div>
    </div>
  );
};

// Progressive Loading Container
interface ProgressiveLoaderProps {
  stages: Array<{
    key: string;
    label: string;
    isLoaded: boolean;
  }>;
  className?: string;
}

export const ProgressiveLoader: React.FC<ProgressiveLoaderProps> = ({
  stages,
  className = ''
}) => (
  <div className={`space-y-2 ${className}`}>
    {stages.map((stage) => (
      <div key={stage.key} className="flex items-center space-x-3">
        <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
          stage.isLoaded ? 'bg-green-500' : 'bg-gray-300 animate-pulse'
        }`}>
          {stage.isLoaded && (
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <span className={`text-sm ${stage.isLoaded ? 'text-green-700' : 'text-gray-500'}`}>
          {stage.label}
        </span>
      </div>
    ))}
  </div>
);

export default Skeleton;
